import { Link } from "react-router-dom";
import {
  ArrowRight, Gavel, FileSignature, Copyright, Building2, ShieldCheck, TrendingUp,
  Check, Circle, Contact as ContactIcon, HelpCircle,
} from "lucide-react";
import { SERVICES, PAIN_POINTS, WHY_HUB, BRAND } from "@/lib/content";
import { ProcessSection, NetworkSection, FaqSection, SectionHead } from "@/components/Sections";
import CTASection from "@/components/CTASection";
import useSEO from "@/lib/useSEO";
import { downloadVCard } from "@/lib/vcard";

const ICONS = { Gavel, FileSignature, Copyright, Building2, ShieldCheck, TrendingUp };

const PLATFORMS = ["YouTube", "TikTok", "Twitch", "Instagram", "Substack", "Patreon", "Spotify", "Kick", "X"];

// An illustration of a request moving through the desk. Labelled "Example" on the card itself.
function ExampleRequest() {
  const steps = [
    { t: "Request received", d: "Mon 9:12 AM", done: true },
    { t: "Reviewed by a licensed attorney", d: "Mon 2:40 PM", done: true },
    { t: "Matched: contracts attorney, licensed in California", d: "Tue 10:05 AM", done: true },
    { t: "Flat-fee quote for the redline", d: "Before any work starts", done: false },
  ];
  return (
    <div className="relative rounded-2xl border border-[#272B36] bg-[#121419]/90 backdrop-blur p-6 sm:p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]" aria-label="Example request">
      <div className="flex items-center justify-between">
        <p className="eyebrow">Example request</p>
        <span className="text-[11px] text-slate-500 border border-[#272B36] rounded-full px-2.5 py-1">Illustration</span>
      </div>
      <p className="mt-4 font-display text-2xl text-[#F4F1EA] leading-snug">
        "A beverage brand sent a 20-page sponsorship contract. They want an answer by Friday."
      </p>
      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        {["Creator contracts", "YouTube + TikTok", "Deadline: Friday"].map((t) => (
          <span key={t} className="rounded-full bg-[#1A1D25] border border-[#272B36] px-3 py-1 text-slate-300">{t}</span>
        ))}
      </div>
      <ol className="mt-6 space-y-4">
        {steps.map((s) => (
          <li key={s.t} className="flex gap-3">
            <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-full flex items-center justify-center ${s.done ? "bg-[#D9B36C] text-[#0B0C10]" : "border border-[#D9B36C]/60 text-[#D9B36C]"}`}>
              {s.done ? <Check size={12} strokeWidth={3} /> : <Circle size={6} fill="currentColor" />}
            </span>
            <div>
              <p className={`text-sm ${s.done ? "text-[#F4F1EA]" : "text-[#D9B36C]"}`}>{s.t}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.d}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function Home() {
  useSEO({
    title: "Upload Counsel | The legal desk for YouTubers, streamers and creators",
    description: "One request for every legal problem a creator has: brand deals, platform bans, trademarks, impersonators, business setup. A licensed attorney reviews it and matches you with vetted counsel.",
    canonical: "https://uploadlawyer.com/",
  });

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 glow-tl" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-16 md:pt-24 md:pb-24 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow fade-in-up">Legal concierge for creators</p>
            <h1 className="mt-5 font-display text-[2.9rem] leading-[1.02] sm:text-6xl lg:text-[4.6rem] text-[#F4F1EA] fade-in-up" style={{ animationDelay: "60ms" }}>
              The legal desk every creator keeps on <span className="italic text-[#D9B36C]">speed dial.</span>
            </h1>
            <p className="mt-6 text-slate-300 text-lg sm:text-xl max-w-xl leading-relaxed fade-in-up" style={{ animationDelay: "120ms" }}>
              Brand deals, platform bans, trademarks, impersonators, business setup. Send one request. A licensed attorney reviews it and matches you with vetted counsel who handles exactly that.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 fade-in-up" style={{ animationDelay: "180ms" }}>
              <Link
                to="/start"
                data-testid="hero-cta-consultation"
                className="inline-flex items-center gap-2 bg-[#D9B36C] text-[#0B0C10] font-semibold px-7 py-4 rounded-full hover:bg-[#E5C688] transition-colors"
              >
                Start a request <ArrowRight size={16} />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="hero-cta-services"
                className="inline-flex items-center gap-2 border border-[#272B36] text-[#F4F1EA] font-medium px-7 py-4 rounded-full hover:bg-white/5 transition-colors"
              >
                How it works
              </Link>
            </div>
            <dl className="mt-10 grid grid-cols-3 gap-4 max-w-lg fade-in-up" style={{ animationDelay: "240ms" }}>
              {[
                ["Free to ask", "No fee to send a request"],
                ["Attorney-reviewed", "Every request, by a licensed lawyer"],
                ["Price first", "Written scope before work"],
              ].map(([k, v]) => (
                <div key={k} className="border-l border-[#D9B36C]/40 pl-3">
                  <dt className="text-sm font-semibold text-[#F4F1EA]">{k}</dt>
                  <dd className="text-xs text-slate-400 mt-1 leading-snug">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="lg:col-span-5 fade-in-up" style={{ animationDelay: "200ms" }}>
            <ExampleRequest />
          </div>
        </div>

        <div className="relative border-y border-[#272B36] bg-[#0B0C10]/80">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-wrap items-center gap-x-7 gap-y-2">
            <span className="text-xs text-slate-500">Built for creators on</span>
            {PLATFORMS.map((p) => (
              <span key={p} className="text-sm text-slate-400">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PRACTICE AREAS */}
      <section className="relative py-20 md:py-28" id="services">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <SectionHead
              eyebrow="What we handle"
              title="Six kinds of trouble. One place to start."
              body="Whatever lands in your inbox, the request is the same. We work out who should handle it."
            />
            <Link to="/services" data-testid="services-overview-cta" className="inline-flex items-center gap-2 text-[#D9B36C] font-medium hover:text-[#F4F1EA] transition-colors shrink-0">
              All practice areas <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Gavel;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-card-${s.slug}`}
                  className="beam-card group rounded-2xl border border-[#272B36] bg-[#121419] p-7 hover:border-[#D9B36C]/40 transition-colors"
                >
                  <Icon size={22} className="text-[#D9B36C]" />
                  <h3 className="mt-5 font-display text-2xl text-[#F4F1EA]">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm text-[#D9B36C] group-hover:gap-2 transition-all">
                    Learn more <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
            <Link
              to="/start"
              className="group sm:col-span-2 lg:col-span-3 rounded-2xl border border-dashed border-[#272B36] p-7 hover:border-[#D9B36C]/50 transition-colors flex flex-col sm:flex-row sm:items-center gap-5"
            >
              <HelpCircle size={22} className="text-slate-400 shrink-0" />
              <div className="flex-1">
                <h3 className="font-display text-2xl text-[#F4F1EA]">Not sure what it is?</h3>
                <p className="mt-1 text-sm text-slate-400 leading-relaxed">Most requests don't fit a box. Describe what happened and we'll sort it.</p>
              </div>
              <span className="inline-flex items-center gap-1 text-sm text-[#D9B36C] group-hover:gap-2 transition-all shrink-0">
                Start a request <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ProcessSection />

      {/* WHY ONE HUB */}
      <section className="relative py-20 md:py-28 border-t border-[#272B36]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHead
              eyebrow="Why one desk"
              title="Every creator needs a lawyer. Few need the same one twice."
              body="Creator law runs from contracts to copyright to platform policy to tax structure. Keep one contact who knows who handles each."
            />
            <div className="mt-8 rounded-2xl border border-[#D9B36C]/30 bg-[#14161D] p-6">
              <p className="font-display text-2xl text-[#F4F1EA]">Save us now. You'll want us later.</p>
              <p className="mt-2 text-sm text-slate-400">Add Upload Counsel to your contacts so the next problem starts with one message.</p>
              <button
                type="button"
                onClick={downloadVCard}
                data-testid="save-contact"
                className="mt-5 inline-flex items-center gap-2 border border-[#D9B36C]/60 text-[#D9B36C] font-medium px-5 py-2.5 rounded-full hover:bg-[#D9B36C]/10 transition-colors"
              >
                <ContactIcon size={16} /> Add to contacts
              </button>
              <p className="mt-3 text-xs text-slate-500">{BRAND.email}</p>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-px rounded-2xl overflow-hidden border border-[#272B36] bg-[#272B36]">
            {WHY_HUB.map((w, i) => (
              <div key={w.h} className="bg-[#0F1116] p-7 md:p-8 flex gap-6">
                <p className="font-display text-4xl text-[#D9B36C]/70 leading-none w-10 shrink-0">{i + 1}</p>
                <div>
                  <p className="font-semibold text-[#F4F1EA] text-lg">{w.h}</p>
                  <p className="mt-2 text-slate-400 leading-relaxed">{w.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NetworkSection />

      {/* SOUND FAMILIAR */}
      <section className="relative py-20 md:py-28 border-t border-[#272B36]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHead eyebrow="Sound familiar?" title="The problems that arrive on a Friday night." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PAIN_POINTS.map((p, i) => (
              <Link
                to="/start"
                key={p.h}
                data-testid={`pain-point-${i}`}
                className="group rounded-2xl border border-[#272B36] bg-[#121419] p-6 hover:border-[#D9B36C]/40 transition-colors"
              >
                <p className="font-semibold text-[#F4F1EA]">{p.h}</p>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.b}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs text-[#D9B36C] opacity-80 group-hover:opacity-100">
                  Get this reviewed <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FaqSection />

      <CTASection
        title="Don't sit on it."
        body="Most creator legal problems are cheaper to sort early than to fight late. Send a request; we aim to reply within one business day."
      />
    </div>
  );
}
